/** @format */

import cn from "classnames";
import { useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Button, Input, OTPInput, Transition, Typography } from "ui";

import { ROLE_ID } from "core-lib/config/Env";
import { API_STATUS } from "core-lib/constants/network";
import { PWA_HOSTNAME } from "core-lib/data/common";
import { isNumeric } from "core-lib/utils/regex";
import { isParkPlusWebView } from "core-lib/utils/source";
import { isServer, isStringValid, isValidJson } from "core-lib/utils/utils";
import { phoneNumberValidation } from "core-lib/utils/validation";
import Cross from "ui/components/newIcons/Cross";
import {
  FINGERPRINT_PSK_ID,
  FINGERPRINT_REQUEST_ID,
  FINGERPRINT_VISITOR_ID,
} from "../../constants";
import { useSendOTP } from "../../hooks/useSendOTP";
import { useVerifyOTP } from "../../hooks/useVerifyOTP";
import { BodyType } from "../../services/verifyOTP";
import s from "./Login.module.scss";
import Resend from "./Resend";

export function Login({
  activeStep,
  onStepChange,
  handlePostLogin = true,
  onVerified = () => {},
  title = "Let’s start with phone number",
  subTitle = "Enter your mobile number to verify",
}: {
  activeStep: number;
  onStepChange: (args: number) => void;
  handlePostLogin?: boolean;
  onVerified?: (args: any) => void;
  title?: string;
  subTitle?: string;
}) {
  let role = "";
  if (window.location.href.includes("role")) {
    const redirect = new URL(window.location.href)?.searchParams.get(
      "redirect"
    );
    if (redirect) {
      role = new URL(`${PWA_HOSTNAME}${redirect}`).searchParams.get("role");
    }
  }

  const [phoneInput, setPhoneInput] = useState({ input: "", err: "" });
  const [otpInput, setOTPInput] = useState({ input: "", err: "" });

  const { sendOtp, resendOtp, apiCall: sendOtpApiCall } = useSendOTP();
  const { init: verifyOtp, apiCall: verifyOtpApiCall } = useVerifyOTP({
    handlePostLogin,
  });

  const { loginPopup, isAuthenticated } = useSelector((state) => state?.auth);

  const sendOtpRef = useRef(false);
  const verifyOtpRef = useRef(false);

  function handleSubmit() {
    if (activeStep === 0) {
      setOTPInput({ input: "", err: "" });
      let errMsg = "";
      if (isStringValid(String(phoneInput.input))) {
        const errObj = phoneNumberValidation(phoneInput.input);
        if (!errObj.isValid) errMsg = errObj.errMsg;
      } else errMsg = "Phone number is required";
      setPhoneInput({ ...phoneInput, err: errMsg });
      if (errMsg === "") {
        sendOtp({ phoneNumber: Number(phoneInput.input) });
        sendOtpRef.current = true;
      }
    }
    if (activeStep === 1) {
      let errMsg = "";

      if (isStringValid(String(otpInput.input))) {
        const isValid =
          isNumeric(otpInput.input) && String(otpInput.input).length === 4;
        if (!isValid) errMsg = "Enter valid OTP";
      } else {
        errMsg = "Enter OTP received on your phone";
      }
      setOTPInput({ ...otpInput, err: errMsg });
      if (!isStringValid(errMsg)) {
        const reqBody: BodyType = {
          otp: otpInput.input,
          state: sendOtpApiCall.data.state,
          phoneNumber: phoneInput.input,
        };
        if (!isServer()) {
          const fingerPrintRequestId = window.sessionStorage.getItem(
            FINGERPRINT_REQUEST_ID
          );
          const fingerPrintVisitorId = window.sessionStorage.getItem(
            FINGERPRINT_VISITOR_ID
          );
          const fingerPrintPskId =
            window.sessionStorage.getItem(FINGERPRINT_PSK_ID);
          if (
            fingerPrintRequestId &&
            fingerPrintVisitorId &&
            fingerPrintPskId
          ) {
            reqBody.fingerprintRequestId = fingerPrintRequestId;
            reqBody.fingerprintVisitorId = fingerPrintVisitorId;
            reqBody.fingerprintPskId = fingerPrintPskId;
          }
        }

        if (role && !!ROLE_ID[role]) {
          reqBody.verificationType = "otp";
          reqBody.roleId = ROLE_ID[role];
        }

        const deepLinkData = global.sessionStorage.getItem("DEEPLINK_DATA");

        if (isValidJson(deepLinkData)) {
          reqBody.deepLinkData = JSON.parse(deepLinkData);
        }

        verifyOtpRef.current = true;
        verifyOtp(reqBody);
      }
    }
  }

  useEffect(()=>{
    if(loginPopup.autoFillPhoneNo){
      setPhoneInput({input:loginPopup.autoFillPhoneNo,err:""})
    }
  },[loginPopup.autoFillPhoneNo])

  useEffect(() => {
    if (sendOtpApiCall.apiStatus === API_STATUS.success) {
      onStepChange(1);
      toast.success("OTP Sent to your phone number");
      sendOtpRef.current = false;
    }
    if (sendOtpApiCall.apiStatus === API_STATUS.failure) {
      toast.error("There was a problem sending otp, please try again");
      sendOtpRef.current = false;
    }
  }, [sendOtpApiCall]);

  useEffect(() => {
    if (verifyOtpApiCall.apiStatus === API_STATUS.success) {
      onStepChange(1);
      onVerified(verifyOtpApiCall.data);
    }
    if (verifyOtpApiCall.apiStatus === API_STATUS.failure) {
      setOTPInput({ input: "", err: "Invalid OTP" });
      verifyOtpRef.current = false;
    }
  }, [verifyOtpApiCall]);

  useEffect(() => {
    if (
      activeStep === 0 &&
      phoneInput.input.length === 10 &&
      !phoneInput.err &&
      !sendOtpRef.current
    ) {
      handleSubmit();
      sendOtpRef.current = true;
    }
  }, [phoneInput]);

  useEffect(() => {
    if (
      activeStep === 1 &&
      otpInput.input.length === 4 &&
      !otpInput.err &&
      !verifyOtpRef.current
    ) {
      handleSubmit();
    }
  }, [otpInput]);

  return (
    <div className={s.root}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Transition
          animation={{
            type: "ltr",
            timeout: "s",
          }}
          show={activeStep === 0}
        >
          <div className={s.cmn_div}>
            <Typography label={title} variant="h2" />
            <Typography
              label={subTitle}
              variant="base_big_r"
              style={{ marginBottom: "36px" }}
              color="text_subheading"
            />
            <div className={cn(s.item, s.itemPhone)}>
              <Input
                onChange={(e: any) => {
                  const { value } = e.target;
                  const invalid = value.length > 10 || !isNumeric(value);
                  if (!invalid) {
                    setPhoneInput({ ...phoneInput, input: value });
                  }
                }}
                name="phone"
                placeholder="92134 22551"
                value={phoneInput.input}
                hasError={phoneInput.err !== ""}
                errorMessage={phoneInput.err}
                // @ts-ignore
                inputMode="numeric"
                autoFocus
                leftIcon={<Typography label="+91" variant="large_input" />}
                rightIcon={
                  phoneInput.input && (
                    <div
                      onClick={() => {
                        setPhoneInput({ input: "", err: "" });
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <Cross />
                    </div>
                  )
                }
                renderType="StandAloneInput"
              />
            </div>
          </div>
        </Transition>

        <Transition
          animation={{
            type: "rtl",
            timeout: "s",
          }}
          show={activeStep === 1}
        >
          <div className={s.cmn_div}>
            <Typography
              label="Confirm your phone number"
              variant="h2"
              style={{ paddingBottom: "6px" }}
            />
            <Typography
              label="Please verify your phone number by entering the verification code sent to"
              variant="base_big_r"
              style={{ marginBottom: "12px" }}
              color="text_subheading"
            />

            <div className={s.editBtn}>
              <Typography label={`+91 ${phoneInput.input}`} variant="h5" />
              <Typography
                label="Edit"
                variant="Button_Button_small"
                color="interaction_button"
                onClick={() => onStepChange(0)}
              />
            </div>

            <div className={cn(s.item, s.itemOtp)}>
              <OTPInput
                label=""
                onChange={(value) => setOTPInput({ ...otpInput, input: value })}
                hasError={otpInput.err !== ""}
                errorText={otpInput.err}
                value={otpInput.input}
                readOnly={false}
                customInputClass={s.otpInput}
                placeholder="••••"
              />
            </div>
          </div>
        </Transition>

        <div className={s.footer}>
          {!isParkPlusWebView() && activeStep === 0 && (
            <div className="termsAndCondition">
              <Typography
                label="By joining Park+ you agree with our"
                variant="small_m"
                style={{ textAlign: "center" }}
                color="text_subheading"
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  justifyContent: "center",
                }}
              >
                <Typography
                  label="Terms of services"
                  variant="small_para_b"
                  onClick={() => {
                    window.location.href =
                      "https://parkplus.io/terms-conditions";
                  }}
                />
                <Typography
                  label=" and "
                  variant="small_m"
                  color="text_subheading"
                />
                <Typography
                  label="Privacy Policy"
                  variant="small_para_b"
                  onClick={() => {
                    window.location.href = "https://parkplus.io/privacy-policy";
                  }}
                />
              </div>
            </div>
          )}

          {activeStep === 1 && (
            <Resend
              onResend={() =>
                resendOtp({
                  phone_number: phoneInput.input,
                  state: sendOtpApiCall.data.state,
                  medium: 1,
                })
              }
            />
          )}

          <Button
            label={activeStep === 0 ? "Next" : "Verify OTP"}
            fullwidth
            onClick={handleSubmit}
            isLoading={
              sendOtpApiCall.apiStatus === API_STATUS.in_progress ||
              verifyOtpApiCall.apiStatus === API_STATUS.in_progress
            }
            isDisabled={
              phoneInput.input.length !== 10 ||
              sendOtpApiCall.apiStatus === API_STATUS.in_progress ||
              verifyOtpApiCall.apiStatus === API_STATUS.in_progress
            }
            customStyle={{ marginTop: 25, marginBottom: 16 }}
            type="submit"
            itemType="submit"
          />
        </div>
      </form>
    </div>
  );
}
